/**
 * 🌟 WELCOME MESSAGE - Machine à positiver SPATIALE !
 * Composant d'accueil avec design épique spatial
 */

import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import getPositiveMessage from '../../utils/positiveMessages';

const WelcomeMessage = ({ customer, isDarkMode }) => {
  // Générer un message unique à chaque connexion
  const positiveMessage = useMemo(() => {
    if (!customer?.name) {
      return { greeting: '👋 Bienvenue !', subtext: 'Content de te voir !' };
    }
    const firstName = customer.name.split(' ')[0];
    return getPositiveMessage(firstName);
  }, [customer?.name]);

  const firstName = customer?.name?.split(' ')[0] || 'Voyageur';

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: -20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.6, type: 'spring', stiffness: 200 }}
      className="max-w-4xl mx-auto mb-6"
    >
      <motion.div 
        className="relative overflow-hidden rounded-2xl p-6 text-center"
        style={{
          background: 'linear-gradient(135deg, #1a0a2e 0%, #16213e 50%, #0f0c29 100%)',
          border: '2px solid transparent',
          borderImage: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 50%, #ec4899 100%) 1',
          boxShadow: '0 0 30px rgba(236, 72, 153, 0.3), 0 0 60px rgba(139, 92, 246, 0.2), inset 0 0 40px rgba(139, 92, 246, 0.1)'
        }}
        whileHover={{ scale: 1.01 }}
      >
        {/* 🌟 Bordure glow animée */}
        <div 
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{
            border: '2px solid rgba(236, 72, 153, 0.5)',
            boxShadow: '0 0 20px rgba(236, 72, 153, 0.4), inset 0 0 20px rgba(139, 92, 246, 0.1)'
          }}
        />

        {/* ✨ Étoiles dorées animées */}
        <motion.div
          className="absolute top-4 left-8 text-yellow-400 text-xl"
          animate={{
            y: [0, -5, 0],
            opacity: [0.6, 1, 0.6],
            scale: [1, 1.2, 1],
            rotate: [0, 15, 0]
          }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          ✦
        </motion.div>

        <motion.div
          className="absolute top-3 right-12 text-yellow-300 text-2xl"
          animate={{
            y: [0, -8, 0],
            opacity: [0.5, 1, 0.5],
            scale: [1, 1.3, 1],
            rotate: [0, -10, 0]
          }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
        >
          ✦
        </motion.div>

        <motion.div
          className="absolute bottom-4 right-8 text-yellow-400 text-lg"
          animate={{
            y: [0, -6, 0],
            opacity: [0.4, 0.9, 0.4],
            scale: [1, 1.15, 1]
          }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
        >
          ⭐
        </motion.div>

        {/* 🎇 Particules de fond */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-purple-400"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                opacity: Math.random() * 0.3 + 0.1
              }}
              animate={{
                opacity: [0.1, 0.4, 0.1],
                scale: [1, 1.5, 1]
              }}
              transition={{
                duration: 2 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2
              }}
            />
          ))}
        </div>

        {/* 💜 Main greeting avec étoile */}
        <div className="relative z-10 flex items-center justify-center gap-2">
          <motion.span
            className="text-yellow-400 text-2xl"
            animate={{ 
              rotate: [0, 10, -10, 0],
              scale: [1, 1.2, 1]
            }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            ✦
          </motion.span>
          
          <motion.h2 
            className="text-2xl md:text-4xl font-black text-white"
            data-testid="welcome-message"
            animate={{ scale: [1, 1.02, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{ 
              textShadow: '0 0 20px rgba(255, 255, 255, 0.5), 0 0 40px rgba(139, 92, 246, 0.3)'
            }}
          >
            Hello {firstName} !
          </motion.h2>

          <motion.span
            className="text-yellow-300 text-xl"
            animate={{ 
              rotate: [0, -15, 15, 0],
              scale: [1, 1.3, 1]
            }}
            transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
          >
            ✦
          </motion.span>
        </div>
        
        {/* 💜 Subtext avec gradient et icônes orbitales */}
        <div className="relative z-10 flex items-center justify-center gap-3 mt-3">
          <motion.span
            className="text-pink-400 text-xl"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            💜
          </motion.span>
          
          <motion.p 
            className="text-base md:text-xl font-medium"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            style={{
              background: 'linear-gradient(90deg, #f472b6, #c084fc, #818cf8)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            {positiveMessage.subtext}
          </motion.p>

          {/* Icônes orbitales style spatial */}
          <motion.span
            className="text-cyan-400 text-sm opacity-60"
            animate={{ 
              rotate: [0, 360],
              scale: [1, 1.1, 1]
            }}
            transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
          >
            💫
          </motion.span>
        </div>

        {/* ✨ Effet shimmer */}
        <motion.div
          className="absolute inset-0 pointer-events-none rounded-2xl"
          style={{
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
          }}
          animate={{
            x: ['-100%', '200%']
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            repeatDelay: 2
          }}
        />
      </motion.div>
    </motion.div>
  );
};

export default WelcomeMessage;
